import React, { useEffect, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { ITask } from '../../interfaces/ITasks';
import { useApiClient } from '../../ApiClientProvider';
import { fetchUsers } from '../../redux/usersSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { useNotifications } from '@toolpad/core';
import { Assignment } from '@mui/icons-material';
import { Dialog, DialogTitle, Box, Typography, DialogContent, Grid, TextField, MenuItem, Chip, Skeleton, Avatar, DialogActions, Button } from '@mui/material';
import { EventTaskStatus } from '../../enums/EventTaskStatus';
import { EventService } from '../../services/eventService';
import { MentionTextField, MentionValue } from '../common/MentionTextField';

interface AddTaskModalProps {
    open: boolean;
    onClose: () => void;
    onAdd: (task: ITask) => void;
    eventId: string;
    events?: Array<{ id: string; title: string }>;
}

const mapStatusToNumber = (status: EventTaskStatus): number => {
    switch (status) {
        case EventTaskStatus.New:
            return 0;
        case EventTaskStatus.Active:
            return 1;
        case EventTaskStatus.Closed:
            return 2;
        default:
            return 0;
    }
};

const statusConfig = {
    New: { label: 'New', color: '#2196f3' },
    Active: { label: 'Active', color: '#ff9800' },
    Closed: { label: 'Closed', color: '#4caf50' },
};

const AddTaskModal: React.FC<AddTaskModalProps> = ({
    open,
    onClose,
    onAdd,
    eventId,
    events,
}) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [descriptionMentions, setDescriptionMentions] = useState<MentionValue[]>([]);
    const [status, setStatus] = useState<EventTaskStatus>(EventTaskStatus.New);
    const [assignedTo, setAssignedTo] = useState('');
    const [dueDate, setDueDate] = useState<Dayjs | null>(dayjs().add(7, 'day'));
    const [loading, setLoading] = useState(false);
    const [assignedToTouched, setAssignedToTouched] = useState(false);
    const [selectedEventForAdd, setSelectedEventForAdd] = useState<string>(eventId);

    const dispatch = useDispatch<AppDispatch>();
    const { users, loading: usersLoading } = useSelector((state: RootState) => state.users);

    const apiClient = useApiClient('applicationClient');
    const apiClientforUser = useApiClient('managementClient');
    const eventService = new EventService(apiClient);
    const notifications = useNotifications();

    useEffect(() => {
        if (open && users.length === 0) {
            dispatch(fetchUsers(apiClientforUser));
        }
    }, [open, dispatch, apiClientforUser, users.length, eventId]);

    const isValidDayjs = (date: Dayjs | null): date is Dayjs => {
        return date !== null && dayjs(date).isValid();
    };

    const resetForm = () => {
        setTitle('');
        setDescription('');
        setDescriptionMentions([]);
        setStatus(EventTaskStatus.New);
        setAssignedTo('');
        setDueDate(dayjs().add(7, 'day'));
        setAssignedToTouched(false);
        setSelectedEventForAdd(eventId);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSubmit = async () => {
        if (!title.trim() || !assignedTo || !isValidDayjs(dueDate)) return;
        setLoading(true);

        const taskDataForApi: ITask = {
            title: title.trim(),
            description: description?.trim() || "",
            assignedTo: assignedTo,
            dueDate: dueDate.toDate(), // Use Date object for ITask
            status: mapStatusToNumber(status),
            mentionedUserIds: descriptionMentions.map((mention) => mention.userId),
        };

        try {
            const newTask = await eventService.AddTask(selectedEventForAdd, taskDataForApi);
            onAdd({
                ...taskDataForApi,
                description: taskDataForApi.description,
                assignedTo: taskDataForApi.assignedTo,
                dueDate: dueDate.toDate(),
                status,
                id: newTask?.id || Date.now().toString(),
                createdAt: new Date(),
                eventId: selectedEventForAdd,
                eventTitle: events?.find(e => e.id === selectedEventForAdd)?.title,
                mentionedUserIds: taskDataForApi.mentionedUserIds,
            });

            resetForm();
            onClose();
        } catch (err: unknown) {
            let errorMessage = 'Unknown error occurred';
            if (
                typeof err === 'object' &&
                err !== null &&
                'response' in err &&
                typeof (err as { response?: { data?: unknown } }).response === 'object' &&
                (err as { response?: { data?: unknown } }).response !== null
            ) {
                const response = (err as { response?: { data?: unknown } }).response;
                const data = (response as { data?: unknown }).data;
                if (typeof data === 'string') {
                    errorMessage = data;
                } else if (typeof data === 'object' && data !== null) {
                    if ('message' in data && typeof (data as { message?: string }).message === 'string') {
                        errorMessage = (data as { message: string }).message;
                    } else if ('title' in data && typeof (data as { title?: string }).title === 'string') {
                        errorMessage = (data as { title: string }).title;
                    } else if ('errors' in data && typeof (data as { errors?: Record<string, string[]> }).errors === 'object') {
                        const validationErrors = Object.values((data as { errors: Record<string, string[]> }).errors).flat();
                        errorMessage = validationErrors.join(', ');
                    } else {
                        errorMessage = JSON.stringify(data);
                    }
                } else {
                    errorMessage = JSON.stringify(data);
                }
            } else if (
                typeof err === 'object' &&
                err !== null &&
                'message' in err &&
                typeof (err as { message?: string }).message === 'string'
            ) {
                errorMessage = (err as { message: string }).message;
            }
            notifications.show(`Failed to create task: ${errorMessage}`,
                {
                    autoHideDuration: 3000,
                    severity: 'error'
                });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 2
                    }
                }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Assignment color="primary" />
                    <Typography variant="h6" fontWeight="600">
                        Create New Task
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                <Grid container spacing={2}>
                    {events && events.length > 1 && (
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label="Event"
                                select
                                fullWidth
                                size="small"
                                value={selectedEventForAdd}
                                onChange={(e) => setSelectedEventForAdd(e.target.value)}
                                sx={{ mt: 1 }}
                            >
                                {events.map(evt => (
                                    <MenuItem key={evt.id} value={evt.id}>{evt.title}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    )}
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            label="Task Title"
                            fullWidth
                            size="small"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Enter a clear, descriptive task title"
                            required
                            error={!title.trim() && title !== ''}
                            helperText={!title.trim() && title !== '' ? 'Title is required' : ''}
                            sx={{ mt: 1 }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <MentionTextField
                            label="Description"
                            multiline
                            rows={3}
                            value={description}
                            mentions={descriptionMentions}
                            candidates={users.flatMap((user) => {
                                const userId = user.userId ?? user.id;
                                if (!userId) return [];
                                const displayName = `${user.firstName} ${user.lastName}`.trim() || user.email;
                                return [{ id: userId, displayName, imageUrl: user.imageUrl }];
                            })}
                            onChange={(value, mentions) => {
                                setDescription(value);
                                setDescriptionMentions(mentions);
                            }}
                            placeholder="Provide detailed information about what needs to be done"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Status"
                            select
                            fullWidth
                            size="small"
                            value={status}
                            onChange={(e) => setStatus(Number(e.target.value) as EventTaskStatus)}
                        >
                            {Object.entries(statusConfig).map(([key, config]) => (
                                <MenuItem key={key} value={EventTaskStatus[key as keyof typeof EventTaskStatus]}>
                                    <Chip
                                        label={config.label}
                                        size="small"
                                        sx={{
                                            backgroundColor: config.color,
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '0.7rem',
                                            height: 20,
                                        }}
                                    />
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Assigned To"
                            select
                            fullWidth
                            size="small"
                            value={assignedTo}
                            onChange={(e) => {
                                setAssignedTo(e.target.value);
                                setAssignedToTouched(true);
                            }}
                            onBlur={() => setAssignedToTouched(true)}
                            required
                            error={assignedToTouched && !assignedTo}
                            helperText={assignedToTouched && !assignedTo ? 'Please select an assignee' : ''}
                        >
                            {usersLoading ? (
                                <MenuItem disabled>
                                    <Skeleton variant="text" width={120} />
                                </MenuItem>
                            ) : users.length === 0 ? (
                                <MenuItem disabled>
                                    <Typography color="text.secondary">No users found</Typography>
                                </MenuItem>
                            ) : (
                                users.map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar sx={{ width: 20, height: 20, fontSize: '0.75rem' }} src={user.imageUrl}>
                                                {user.firstName?.charAt(0) || user.email?.charAt(0)}
                                            </Avatar>
                                            <Typography variant="body2">
                                                {user.firstName} {user.lastName}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))
                            )}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <DatePicker
                            label="Due Date"
                            value={dueDate}
                            onChange={(newValue) => setDueDate(newValue)}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'small',
                                    required: true,
                                    error: !isValidDayjs(dueDate),
                                    helperText: !isValidDayjs(dueDate) ? 'Due date is required' : ''
                                }
                            }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{
                pt: 2
            }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    size="small"
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    size="small"
                    disabled={!title.trim() || !assignedTo || !dueDate || loading}
                >
                    {loading ? 'Creating...' : 'Create Task'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddTaskModal;